import React, { useState, useEffect, useCallback } from 'react';
import { Bell, Calendar, FileText, MessageSquare, CheckSquare, X, Loader2, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { ScrollArea } from './ui/scroll-area';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const NotificacionesDropdown = () => {
  const navigate = useNavigate();
  const [notificaciones, setNotificaciones] = useState([]);
  const [totalNoLeidas, setTotalNoLeidas] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const loadNotificaciones = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/notificaciones`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotificaciones(response.data.notificaciones || []);
      setTotalNoLeidas(response.data.no_leidas || 0);
    } catch (error) {
      console.error('Error cargando notificaciones:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Cargar notificaciones al montar y cada 60 segundos
  useEffect(() => {
    loadNotificaciones();
    const interval = setInterval(loadNotificaciones, 60000);
    return () => clearInterval(interval);
  }, [loadNotificaciones]);

  // Recargar cuando se abre el dropdown
  useEffect(() => {
    if (isOpen) {
      loadNotificaciones();
    }
  }, [isOpen, loadNotificaciones]);

  const handleNotificacionClick = async (notificacion) => {
    // Marcar como leída si es un mensaje
    if (notificacion.tipo === 'mensaje') {
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${API}/api/notificaciones/${notificacion.id}/marcar-leida`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        console.error('Error marcando notificación:', error);
      }
    }
    
    // Navegar a la URL
    if (notificacion.url) {
      setIsOpen(false);
      navigate(notificacion.url);
    }
  };

  const getIconForType = (tipo) => {
    switch (tipo) {
      case 'tarea': return <CheckSquare className="w-4 h-4 text-orange-500" />;
      case 'evento': return <Calendar className="w-4 h-4 text-blue-500" />;
      case 'expediente': return <FileText className="w-4 h-4 text-green-500" />;
      case 'mensaje': return <MessageSquare className="w-4 h-4 text-purple-500" />;
      default: return <Bell className="w-4 h-4 text-gray-500" />;
    }
  };

  const getPrioridadColor = (prioridad) => {
    switch (prioridad) {
      case 'urgente': return 'bg-red-100 text-red-700 border-red-200';
      case 'alta': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'media': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const formatFecha = (fechaStr) => {
    if (!fechaStr) return '';
    const fecha = new Date(fechaStr);
    const hoy = new Date();
    const diff = Math.floor((fecha - hoy) / (1000 * 60 * 60 * 24));
    
    if (diff === 0) return 'Hoy';
    if (diff === 1) return 'Mañana';
    if (diff === -1) return 'Ayer';
    
    return fecha.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative text-white hover:bg-white/10">
          <Bell className="w-5 h-5" />
          {totalNoLeidas > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-[10px] font-bold text-white animate-pulse">
              {totalNoLeidas > 9 ? '9+' : totalNoLeidas}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[380px] p-0">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-gray-600" />
            <span className="font-semibold text-gray-800">Notificaciones</span>
            {totalNoLeidas > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                {totalNoLeidas}
              </span>
            )}
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={loadNotificaciones}
            disabled={loading}
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Actualizar'}
          </Button>
        </div>

        {/* Lista de notificaciones */}
        <ScrollArea className="h-[400px]">
          {loading && notificaciones.length === 0 ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : notificaciones.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-gray-500">
              <Bell className="w-8 h-8 mb-2 text-gray-300" />
              <p className="text-sm">No hay notificaciones</p>
              <p className="text-xs text-gray-400 mt-1">¡Todo está al día!</p>
            </div>
          ) : (
            <div className="divide-y">
              {notificaciones.map((notif) => (
                <div
                  key={notif.id}
                  onClick={() => handleNotificacionClick(notif)}
                  className={`p-3 hover:bg-gray-50 cursor-pointer transition-colors ${
                    !notif.leida ? 'bg-blue-50/50' : ''
                  }`}
                >
                  <div className="flex gap-3">
                    <div className="mt-0.5">
                      {getIconForType(notif.tipo)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-medium text-gray-900 text-sm truncate">
                          {notif.titulo}
                        </p>
                        {notif.prioridad && notif.prioridad !== 'baja' && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded border ${getPrioridadColor(notif.prioridad)}`}>
                            {notif.prioridad === 'urgente' ? '⚡ URGENTE' : notif.prioridad.toUpperCase()}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">
                        {notif.descripcion}
                      </p>
                      <div className="flex items-center justify-between mt-1.5">
                        <span className="text-[10px] text-gray-400 uppercase tracking-wide">
                          {notif.tipo}
                        </span>
                        {notif.fecha && (
                          <span className="text-[10px] text-gray-400">
                            {formatFecha(notif.fecha)}
                          </span>
                        )}
                      </div>
                    </div>
                    <ExternalLink className="w-3 h-3 text-gray-300 mt-1 flex-shrink-0" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        {notificaciones.length > 0 && (
          <div className="border-t px-4 py-2 bg-gray-50">
            <p className="text-xs text-gray-500 text-center">
              Mostrando {notificaciones.length} notificaciones
            </p>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificacionesDropdown;
